import React from 'react';
import { motion, useTransform } from 'framer-motion';
import { TWILIGHT_NAVY, LIVING_CORAL, SANDY_CORAL, SHELL_WHITE, alpha } from '../../../theme/colors';
import { useScrollStage } from '../../../hooks/useScrollStage';
import Label from '../../shared/Label';

// Mirrors the real Weight Interpretation card content (weekly_state_snapshots
// .weight_interpretation shape: direction/primaryMechanism/narrative/tags) —
// sample values only, not a live user's data. Same markup at both
// breakpoints; the Feature card wrapper controls how much room it gets.
// Opaque white card (approved spec) — a glass-on-photo variant was explored
// and rejected in favor of this for Feature 4. Chart height/padding/font
// sizes are noticeably smaller than the 1:1 Paper spec (width stays at 332
// to avoid forcing the description into extra line-wraps) — at Paper's exact
// vertical spacing this card's last description line got clipped by the
// Feature card's fixed 644x580 frame. Diverging from Paper here on purpose.

const CardChrome = ({ pathLength, fillOpacity, contentOpacity, children }) => (
  <>
    <div style={{ position: 'relative', width: '100%', height: 60, flexShrink: 0 }}>
      <svg viewBox="0 0 332 90" width="100%" height="60" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0 }}>
        <defs>
          <linearGradient id="weightChartFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={LIVING_CORAL} stopOpacity="0.2" />
            <stop offset="100%" stopColor={LIVING_CORAL} stopOpacity="0" />
          </linearGradient>
          <linearGradient id="weightChartStroke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={SANDY_CORAL} />
            <stop offset="100%" stopColor={LIVING_CORAL} />
          </linearGradient>
        </defs>
        <motion.path
          d="M0 58C23.298 53 36.112 51 47.179 49 63.488 46 80.379 38 95.523 34 110.667 30 127.558 30 142.702 30 157.846 30 174.154 30 189.298 30 204.442 30 221.333 35 236.477 41 251.621 46 268.512 45 283.656 49 298.8 53 315.109 53 332 55L332 90 0 90Z"
          fill="url(#weightChartFill)"
          style={{ opacity: fillOpacity }}
        />
        <motion.path
          d="M0 58C23.298 53 36.112 51 47.179 49 63.488 46 80.379 38 95.523 34 110.667 30 127.558 30 142.702 30 157.846 30 174.154 30 189.298 30 204.442 30 221.333 35 236.477 41 251.621 46 268.512 45 283.656 49 298.8 53 315.109 53 332 55"
          fill="none"
          stroke="url(#weightChartStroke)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ pathLength }}
        />
      </svg>
    </div>

    <motion.div
      initial={false}
      animate={{ opacity: contentOpacity }}
      transition={{ duration: 0.3 }}
      style={{ display: 'flex', flexDirection: 'column', padding: '14px 18px 18px' }}
    >
      {children}
    </motion.div>
  </>
);

const CardContents = () => (
  <>
    <Label muted style={{ fontFamily: 'DM Sans', fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
      Weight · 14 days
    </Label>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 5, gap: 8 }}>
      <span style={{ fontFamily: 'Instrument Serif', fontStyle: 'italic', fontSize: 18, lineHeight: 1.15, color: TWILIGHT_NAVY }}>
        Fluid Retention Pattern
      </span>
      <span style={{ flexShrink: 0, fontFamily: 'DM Sans', fontSize: 12, fontWeight: 500, padding: '3px 9px', borderRadius: 999, background: alpha('coral', 0.1), color: TWILIGHT_NAVY }}>
        +2.6 lbs
      </span>
    </div>
    <Label as="span" tone={alpha('navy', 0.72)} style={{ display: 'block', width: '100%', fontFamily: 'DM Sans', fontSize: 12, lineHeight: 1.5, marginTop: 8 }}>
      Sleep disruption across 4 nights elevated cortisol, prompting your body to hold fluid — not fat accumulation. Resolution typically
      follows within 5–7 days of sleep stabilising.
    </Label>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
      {['Sleep disrupted ×4', 'Wired + tired ×2'].map((tag) => (
        <span
          key={tag}
          style={{
            fontFamily: 'DM Sans',
            fontSize: 9,
            color: alpha('navy', 0.7),
            padding: '3px 9px',
            borderRadius: 999,
            background: alpha('navy', 0.05),
            border: `1px solid ${alpha('navy', 0.08)}`,
          }}
        >
          {tag}
        </span>
      ))}
    </div>
  </>
);

const cardShellStyle = {
  width: 332,
  maxWidth: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
  backgroundColor: SHELL_WHITE,
};

const StaticWeightInterpretationCard = () => (
  <div style={cardShellStyle}>
    <CardChrome pathLength={1} fillOpacity={1} contentOpacity={1}>
      <CardContents />
    </CardChrome>
  </div>
);

// Different mechanism than before: the card's own appearance and the
// content's appearance are both discrete SNAPS (useScrollStage + a fixed
// real-time transition, same grammar as the checkbox/chip/button snaps on
// cards 1-3) rather than continuous scroll-scrubbed fades. Only the chart
// draw stays scroll-scrubbed, since it's the one genuinely partial value
// here. Scrubbing card-opacity and content-opacity across overlapping or
// adjacent scroll windows was what read as "too many things happening at
// once" — a snap is a single quick, complete event, so at most two things
// (a snap + the ongoing chart draw) are ever animating simultaneously.
//
// Beats (progress 0-1 across the card's pinned scroll runway):
// 0.08 -> blank white card snaps into view (no chart, no content yet)
// 0.12-0.42 chart line draws in (pathLength), fill area fades in within this window
// 0.50 -> contents snap into view, well after the chart has finished
// hold before/after each beat
const AnimatedWeightInterpretationCard = ({ progress }) => {
  const cardVisible = useScrollStage(progress, 0.08);
  const contentVisible = useScrollStage(progress, 0.5);
  const pathLength = useTransform(progress, [0.12, 0.42], [0, 1]);
  const fillOpacity = useTransform(progress, [0.18, 0.4], [0, 1]);

  return (
    <motion.div
      initial={false}
      animate={{ opacity: cardVisible ? 1 : 0, y: cardVisible ? 0 : 24 }}
      transition={{ duration: 0.35 }}
      style={cardShellStyle}
    >
      <CardChrome pathLength={pathLength} fillOpacity={fillOpacity} contentOpacity={contentVisible ? 1 : 0}>
        <CardContents />
      </CardChrome>
    </motion.div>
  );
};

const WeightInterpretationCard = ({ progress }) => {
  if (!progress) return <StaticWeightInterpretationCard />;
  return <AnimatedWeightInterpretationCard progress={progress} />;
};

export default WeightInterpretationCard;
